import React from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Card, CardContent, Typography, Grid, Chip } from "@material-ui/core";
import { CrudOptions, TextClip, Lookup } from "frame/components";
import { useRouter, isStaffUser, createRowSelector, useTerminology } from "utils";
import { SITES_PATH } from "../actions/types";

const useStyles = makeStyles((theme) => ({
	card: {
		display: "flex",
		flexWrap: "wrap",
		position: "relative",
		marginBottom: theme.spacing(2),
	},
	container: {
		margin: theme.spacing(0.5),
	},
	content: {
		padding: theme.spacing(2),
		flexGrow: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			flexWrap: "wrap",
		},
		"&:last-child": {
			paddingBottom: theme.spacing(2),
		},
	},
	label: {
		padding: theme.spacing(0, 0, 0.5, 0),
		color: theme.palette.text.tertiary,
	},
	value: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		padding: theme.spacing(0, 0, 0.5, 0),
	},
	valueInactive: {
		color: theme.palette.text.tertiary,
	},
	error: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
	success: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.white,
	},
}));

const noop = () => {};

const SiteListing = ({
	site,
	className,
	role,
	onEdit,
	onDelete,
	onSelectSite = noop,
	...rest
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const { siteLabel, customerLabel } = useTerminology();
	const { id } = site;

	const inactive = String(site.status).toLowerCase() === "inactive";
	const active = !inactive;

	const onRowSelect = createRowSelector(() => {
		onSelectSite(id);
		history.push(`${SITES_PATH}/${id}`);
	});

	const valueStyle = clsx(classes.value, inactive ? classes.valueInactive : "");

	const addressParts = [site.address, site.suburb, site.townCity].filter(Boolean);
	const contactParts = [
		site.siteContactPerson,
		site.siteContactPhone,
		site.siteContactEmail,
	].filter(Boolean);

	const deleteWarning = (
		<>
			Warning, deleting the site &quot;{site.siteName}&quot; is permanent and cannot be undone.
		</>
	);

	return (
		<Card
			{...rest}
			data-cy={`site-${site.siteName}`}
			className={clsx(classes.card, className)}
			onClick={onRowSelect}
			style={{ cursor: "pointer" }}
		>
			<CardContent className={classes.content}>
				<Grid container spacing={2} className={classes.container}>
					<Grid item lg={2} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							{siteLabel} Name
						</Typography>
						<Typography variant="h6" className={valueStyle}>
							<TextClip hideSeeMore text={site.siteName || "-"} numberOfCharacters={60} />
						</Typography>
					</Grid>
					<Grid item lg={2} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							{customerLabel} Name
						</Typography>
						<Typography variant="h6" className={valueStyle}>
							<Lookup id={site.customerId} resource="customers" attribute="customerName" />
						</Typography>
					</Grid>
					<Grid item lg={3} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Address
						</Typography>
						{addressParts.length > 0 ? (
							addressParts.map((line, i) => (
								<Typography key={i} variant="h6" className={valueStyle}>
									<TextClip hideSeeMore text={line} numberOfCharacters={60} />
								</Typography>
							))
						) : (
							<Typography variant="h6" className={valueStyle}>
								-
							</Typography>
						)}
					</Grid>
					<Grid item lg={3} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Contact Person
						</Typography>
						{contactParts.length > 0 ? (
							contactParts.map((line, i) => (
								<Typography key={i} variant="h6" className={valueStyle}>
									<TextClip hideSeeMore text={line} numberOfCharacters={60} />
								</Typography>
							))
						) : (
							<Typography variant="h6" className={valueStyle}>
								-
							</Typography>
						)}
					</Grid>
					<Grid item lg={1} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Status
						</Typography>
						<Chip
							className={active ? classes.success : classes.error}
							size="small"
							label={active ? "Active" : "Inactive"}
						/>
					</Grid>
				</Grid>
				{!isStaffUser(role) && (
					<div className="ignore">
						<CrudOptions
							onEdit={() => onEdit(id)}
							onDelete={() => onDelete(id)}
							customRemoveModelText={deleteWarning}
						/>
					</div>
				)}
			</CardContent>
		</Card>
	);
};

SiteListing.propTypes = {
	role: PropTypes.string.isRequired,
	className: PropTypes.string,
	site: PropTypes.object.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onSelectSite: PropTypes.func,
};

export default SiteListing;
